import React from 'react'
import type { LucideIcon } from 'lucide-react'
import { getScoreColor, getConvRateColor } from '../types'
import { cn } from '@/lib/utils'

interface KpiCardProps {
  title: string
  icon: LucideIcon
  children: React.ReactNode
}

export function KpiCard({ title, icon: Icon, children }: KpiCardProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        <Icon className="w-5 h-5 text-gray-400" />
      </div>
      {children}
    </div>
  )
}

// Total Calls Card
interface TotalCallsProps {
  total: number
  inbound: number
  outbound: number
}

export function TotalCallsContent({ total, inbound, outbound }: TotalCallsProps) {
  return (
    <div>
      <p className="text-4xl font-bold text-gray-900">{total}</p>
      <p className="text-sm text-gray-500 mt-1">
        Inbound {inbound} / Outbound {outbound}
      </p>
    </div>
  )
}

// Bookings Card
interface BookingsProps {
  total: number
  conversionRate: number
  opportunities: number
  booked: number
}

export function BookingsContent({ total, conversionRate, opportunities, booked }: BookingsProps) {
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <p className="text-4xl font-bold text-gray-900">{total}</p>
        <span className={cn("text-sm font-medium px-2 py-0.5 rounded", getConvRateColor(conversionRate))}>
          {conversionRate}% Conv
        </span>
      </div>
      <p className="text-sm text-gray-500 mt-1">
        Opportunities: {opportunities} → {booked} booked
      </p>
    </div>
  )
}

// Avg Quality Score Card
interface QualityScoreProps {
  newOpps: number
  existing: number
  insurance: number
}

export function QualityScoreContent({ newOpps, existing, insurance }: QualityScoreProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500">New Opps</span>
        <span className={cn('text-2xl font-bold px-2 py-0.5 rounded', getScoreColor(newOpps))}>
          {newOpps}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500">Existing</span>
        <span className={cn('text-2xl font-bold px-2 py-0.5 rounded', getScoreColor(existing))}>
          {existing}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500">Insurance</span>
        <span className={cn('text-2xl font-bold px-2 py-0.5 rounded', getScoreColor(insurance))}>
          {insurance}
        </span>
      </div>
    </div>
  )
}

// Missed Calls Card
interface MissedCallsProps {
  total: number
  calledBackCount: number
  lastMissedCall?: {
    fromName: string
    time: string
  } | null
  onViewAll?: () => void
}

export function MissedCallsContent({ total, calledBackCount, lastMissedCall, onViewAll }: MissedCallsProps) {
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <p className={`text-4xl font-bold ${total > 0 ? 'text-red-600' : 'text-gray-900'}`}>
          {total}
        </p>
        {calledBackCount > 0 && (
          <span className="text-lg text-green-600 font-bold">
            ({calledBackCount} called back)
          </span>
        )}
      </div>
      {total > 0 && lastMissedCall ? (
        <div className="mt-2">
          <p className="text-xs text-gray-500">Last missed:</p>
          <p className="text-sm font-medium text-gray-700 truncate" title={lastMissedCall.fromName}>
            {lastMissedCall.fromName}
          </p>
          <p className="text-xs text-gray-400">{lastMissedCall.time}</p>
        </div>
      ) : (
        <p className="text-sm text-gray-500 mt-1">No missed calls</p>
      )}
      {total > 0 && onViewAll && (
        <button
          type="button"
          onClick={onViewAll}
          className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
        >
          View all →
        </button>
      )}
    </div>
  )
}
